import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar
} from '@mui/material';
import { Comment as CommentIcon, Add as AddIcon } from '@mui/icons-material';
import { cardSx,
  headerSx,
  headerLeftSx,
  addButtonSx,
  cardContentSx,
  messageBoxSuccessSx,
  messageBoxErrorSx,
  addCommentBoxSx,
  textFieldSx,
  addCommentActionsSx,
  cancelButtonSx,
  addCommentButtonSx,
  listSx,
  listItemSx,
  commentPrimarySx,
  dividerSx,
  noCommentsBoxSx } from '../../styles/TripComments.styles';
import { api } from '../../../../services/api';
import { formatDateTime } from '../../../../utils/dateFormatter';

interface TripComment {
  id?: number;
  comment: string;
  createdAt: string;
  createdByName: string;
}

interface TripCommentsSectionProps {
  comments: TripComment[];
  onUpdate: (newComment: NewCommentPayload) => Promise<void>;
  loading?: boolean;
  tripCode: string;
  disabled?: boolean
}
interface NewCommentPayload {
  comment: string;
}

const TripCommentsSection: React.FC<TripCommentsSectionProps> = ({
  comments,
  onUpdate,
  loading = false,
  tripCode,
  disabled = false
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);

  useEffect(() => {
    if (shouldPreserveScroll && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setShouldPreserveScroll(false);
      }, 100);
    }
  }, [comments, isAdding, shouldPreserveScroll]);

  const handleAddClick = () => {
    setIsAdding(true);
    setNewComment('');
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewComment('');
    setErrorMessage('');
  };

  const handleSaveNew = async () => {
  setSaving(true);
  setErrorMessage('');
  setShouldPreserveScroll(true);
  if (!newComment.trim()) {
    setErrorMessage('Please enter a comment');
    setSaving(false);
    return;
  }
  try {
    if (onUpdate) {
        // Send only the new comment payload (just the comment string)
        const payload: NewCommentPayload = {
          comment: newComment.trim()
        };
        await onUpdate(payload);
      setSuccessMessage('Comment added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsAdding(false);
      setNewComment('');
    }
  } catch (error: any) {
    setErrorMessage(error?.message || 'Failed to add comment');
  } finally {
    setSaving(false);
  }
};

  const SectionHeader = () => (
    <Box 
      sx={headerSx}
    >
      <Box sx={headerLeftSx}>
        <CommentIcon />
        <Typography variant="h6" fontWeight="bold">
          TRIP COMMENTS ({comments.length})
        </Typography>
      </Box>
      {!isAdding && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          sx={addButtonSx}
          onClick={handleAddClick}
          disabled={loading}
        >
          ADD COMMENT
        </Button>
      )}
    </Box>
  );

  return (
    <Card ref={sectionRef} sx={cardSx}>
      <SectionHeader />
      <CardContent 
        sx={cardContentSx}
      >
        {successMessage && (
          <Box sx={messageBoxSuccessSx}>{successMessage}</Box>
        )}
        {errorMessage && (
          <Box sx={messageBoxErrorSx}>{errorMessage}</Box>
        )}

        {/* Add new comment form */}
        {isAdding && (
          <Box sx={addCommentBoxSx}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              variant="outlined"
              size="small"
              sx={textFieldSx}
            />
            <Box sx={addCommentActionsSx}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleCancel}
                sx={cancelButtonSx}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                size="small"
                onClick={handleSaveNew}
                sx={addCommentButtonSx}
                disabled={saving || !newComment.trim() }
              >
                {saving ? <CircularProgress size={16} /> : 'Add'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Display existing comments */}
        {comments.length > 0 ? (
          <List 
            sx={listSx(isAdding)}
          >
            {comments.map((comment, index) => (
              <Box key={comment.id || index}>
                <ListItem 
                  sx={{
                    ...listItemSx,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    mb: 1,
                    p: 2
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={commentPrimarySx}>
                        <Typography variant="subtitle2" fontWeight="bold" color="white" sx={{ mr: 1 }}>
                          {comment.createdByName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDateTime(comment.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography 
                        variant="body2" 
                        color="rgba(255,255,255,0.8)"
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          p: 1.5,
                          borderRadius: '6px',
                          mt: 0.5
                        }}
                      >
                        {comment.comment}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < comments.length - 1 && <Divider sx={dividerSx} />}
              </Box>
            ))}
          </List>
        ) : !isAdding ? (
          <Box sx={noCommentsBoxSx}>
            <Typography variant="body1" color="textSecondary">
              No comments available for this trip
            </Typography>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default TripCommentsSection;

